import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Search,
  ChevronDown,
  ChevronRight,
  Filter,
  Plus,
  Trash2,
  Pause,
  Play,
  ArrowRight,
  Copy,
  Check,
  X,
  Sparkles,
  ClipboardList,
  BarChart3,
  GitBranch,
  AlertTriangle,
  RefreshCw,
  Shield,
  CheckCircle2,
  XCircle,
  FolderOpen,
  Activity,
  BookOpen,
} from 'lucide-react'
import { KaijuLoader } from '../components/KaijuLoader'
import { AiOpsTab } from '../components/tracker/AiOpsTab'

// ─── Types ───
interface PipelineStage {
  done: boolean
  date: string | null
  file?: string
}

interface TrackerDocument {
  id: string
  slug: string
  folder: string | null
  title: string
  shortTitle: string
  type: string
  seriesGroup: string
  status: string
  lastUpdated: string | null
  pipeline: Record<string, PipelineStage>
  tags: string[]
  summary?: string
  concept?: string
  popCulture?: string
  popCultureNote?: string
  historicalRef?: string
  historicalRefNote?: string
  humanPublishedAt?: string | null
  linkedinPublishedAt?: string | null
  version?: string
}

// ─── Helpers ───
function getCurrentStage(
  pipeline: Record<string, PipelineStage>,
  stages: string[],
  labels: Record<string, string>,
): { step: number; total: number; name: string; date: string | null } {
  if (stages.length === 0) {
    return { step: 0, total: 0, name: '', date: null }
  }
  let lastDoneIndex = -1
  for (let i = stages.length - 1; i >= 0; i--) {
    const s = stages[i] as string
    if (pipeline[s]?.done) {
      lastDoneIndex = i
      break
    }
  }
  if (lastDoneIndex === -1) {
    const first = stages[0] as string
    return { step: 0, total: stages.length, name: labels[first] || first, date: null }
  }
  const key = stages[lastDoneIndex] as string
  return {
    step: lastDoneIndex + 1,
    total: stages.length,
    name: labels[key] || key,
    date: pipeline[key]?.date || null,
  }
}

interface Interrelation {
  source: string
  target: string
  type: string
  description: string
  bidirectional: boolean
}

interface TrackerData {
  meta: {
    generatedAt: string
    generatedBy: string
    totalDocuments: number
    pipelineStages: string[]
    blogPipelineStages?: string[]
    bookPipelineStages?: string[]
    stageLabels: Record<string, string>
    stageAbbreviations: Record<string, string>
  }
  documents: TrackerDocument[]
  crossCorrelation: {
    matrix: Record<string, Record<string, { lastChecked: string | null; fresh: boolean }>>
  }
  interrelations: Interrelation[]
}

// ─── Constants ───
const EDGE_COLORS: Record<string, string> = {
  extends: '#3b82f6',
  feeds: '#f97316',
  unifies: '#a855f7',
  'pairs-with': '#22c55e',
  'depends-on': '#ef4444',
  implements: '#14b8a6',
  supports: '#9ca3af',
}

const STATUS_STYLES: Record<string, string> = {
  final: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
  review: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-400',
  council_reviewed: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-400',
  partial: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
  outlined: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
  'white-paper-only': 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400',
  in_progress: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
  concept: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-400',
  drafted: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-400',
  edited: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-400',
  published: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400',
  backlog: 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-400',
  hold: 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400',
}

const GROUP_LABELS: Record<string, string> = {
  'core-foundation': 'Core Foundation',
  'harness-horizon': 'Harness Horizon',
  'three-body-problem': 'Three-Body Problem',
  'stable-orbit': 'Stable Orbit Security',
  'shatterdome': 'Shatterdome Doctrine',
  'three-surfaces': 'Three Governance Surfaces',
  standalone: 'Standalone White Papers',
  patterns: 'Standalone Patterns',
  'field-manuals': 'Field Manuals',
  'geek-translation': 'The Geek Translation: Culture Meets Code',
}

const GROUP_ORDER = ['core-foundation', 'harness-horizon', 'stable-orbit', 'three-body-problem', 'shatterdome', 'three-surfaces', 'geek-translation', 'standalone', 'field-manuals', 'patterns']

const BOOK_LABELS: Record<string, string> = {
  'TBP-I': 'Vol I: Harness Horizon',
  'TBP-II': 'Vol II: Classified AI',
  'TBP-III': 'Vol III: Lagrange Points',
  'TBP-IV': 'Vol IV: Stable Orbit Security',
  'TBP-V': 'Vol V: Replicant Fleet',
  'TBP-VI': 'Vol VI: Shatterdome Doctrine',
  'TBP-VII': 'Vol VII: Three Governance Surfaces',
}

const BOOK_ORDER = ['TBP-I', 'TBP-II', 'TBP-III', 'TBP-IV', 'TBP-V', 'TBP-VI', 'TBP-VII']

// ─── Analysis Types ───
interface AnalysisFix {
  file: string
  oldText: string
  newText: string
}

interface AnalysisFinding {
  id: string
  severity: 'HIGH' | 'MEDIUM' | 'LOW'
  category: string
  title: string
  documents: string[]
  description: string
  recommendation: string
  fixable: boolean
  fixes?: AnalysisFix[]
}

interface AnalysisResult {
  findings: AnalysisFinding[]
  summary: { high: number; medium: number; low: number; total: number; fixable: number }
  documentsScanned: number
  durationMs: number
}

interface AnalysisProgress {
  step: string
  percent: number
  message: string
  findingsCount: number
}

const SEVERITY_STYLES: Record<string, string> = {
  HIGH: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  MEDIUM: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  LOW: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
}

type TabId = 'pipeline' | 'punchlist' | 'correlation' | 'graph' | 'aiops'

// ─── Component ───
export function TrackerPage() {
  const [activeTab, setActiveTab] = useState<TabId>('pipeline')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterGroup, setFilterGroup] = useState('all')
  const [filterType, setFilterType] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery<TrackerData>({
    queryKey: ['tracker'],
    queryFn: async () => {
      const resp = await fetch('/api/tracker')
      if (!resp.ok) throw new Error('Failed to load tracker data')
      return resp.json()
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <KaijuLoader size="md" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="glass-card-subtle text-center py-12">
        <p className="text-sm text-red-500 dark:text-red-400">
          Failed to load tracker data. Make sure the API server is running.
        </p>
      </div>
    )
  }

  const tabs: { id: TabId; label: string; icon: typeof BarChart3 }[] = [
    { id: 'pipeline', label: 'Pipeline', icon: BarChart3 },
    { id: 'punchlist', label: 'Punch List', icon: ClipboardList },
    { id: 'correlation', label: 'Cross-Correlation', icon: GitBranch },
    { id: 'graph', label: 'Interrelations', icon: Sparkles },
    { id: 'aiops', label: 'AI Ops', icon: Activity },
  ]

  return (
    <div className="space-y-6">
      <Header data={data} />
      <StatsBar data={data} />

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
              activeTab === tab.id
                ? 'text-primary-600 dark:text-primary-400 border-primary-500'
                : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'pipeline' && (
        <PipelineSection
          data={data}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          filterGroup={filterGroup}
          setFilterGroup={setFilterGroup}
          filterType={filterType}
          setFilterType={setFilterType}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          expandedIds={expandedIds}
          setExpandedIds={setExpandedIds}
        />
      )}
      {activeTab === 'punchlist' && (
        <PunchListSection data={data} queryClient={queryClient} />
      )}
      {activeTab === 'correlation' && <CrossCorrelationSection data={data} queryClient={queryClient} />}
      {activeTab === 'graph' && <InterrelationGraph data={data} />}
      {activeTab === 'aiops' && <AiOpsTab />}
    </div>
  )
}

// ─── Header ───
function Header({ data }: { data: TrackerData }) {
  return (
    <div className="flex items-start justify-between">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Pipeline Tracker</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Doctrine Library — White Papers, Books, Blogs & Patterns
        </p>
      </div>
      <div className="text-right text-xs text-gray-400 dark:text-gray-500 font-mono">
        <div>Generated: {new Date(data.meta.generatedAt).toLocaleDateString()}</div>
        <div>Source: {data.meta.generatedBy}</div>
      </div>
    </div>
  )
}

// ─── Stats Bar ───
function StatsBar({ data }: { data: TrackerData }) {
  const docs = data.documents
  const total = docs.length
  const finalCount = docs.filter(d => d.status === 'final').length
  const inProgressCount = docs.filter(d =>
    ['partial', 'outlined', 'white-paper-only'].includes(d.status)
  ).length
  const backlogCount = docs.filter(d => d.status === 'backlog').length
  const holdCount = docs.filter(d => d.status === 'hold').length

  const wpStages = data.meta.pipelineStages
  const blogStages = data.meta.blogPipelineStages || []
  const bookStages = data.meta.bookPipelineStages || []
  let totalStages = 0, doneStages = 0
  docs.forEach(d => {
    const stages = d.type === 'blog' ? blogStages : d.type === 'book' ? bookStages : wpStages
    stages.forEach(s => {
      totalStages++
      if (d.pipeline[s]?.done) doneStages++
    })
  })
  const pipelinePct = totalStages > 0 ? Math.round((doneStages / totalStages) * 100) : 0
  const blogCount = docs.filter(d => d.type === 'blog').length
  const bookCount = docs.filter(d => d.type === 'book').length
  const conceptCount = docs.filter(d => d.status === 'concept').length

  const matrix = data.crossCorrelation.matrix
  const activeIds = docs.filter(d => d.status !== 'backlog' && d.status !== 'hold').map(d => d.id)
  const totalPairs = activeIds.length * (activeIds.length - 1)
  let checkedPairs = 0
  activeIds.forEach(a => {
    if (matrix[a]) {
      activeIds.forEach(b => {
        if (a !== b && matrix[a]?.[b]?.lastChecked) checkedPairs++
      })
    }
  })
  const xcPct = totalPairs > 0 ? Math.round((checkedPairs / totalPairs) * 100) : 0

  const stats = [
    { label: 'Total', value: total, color: '' },
    { label: 'Final', value: finalCount, color: 'text-green-600 dark:text-green-400' },
    { label: 'In Progress', value: inProgressCount, color: 'text-amber-600 dark:text-amber-400' },
    { label: 'Backlog', value: backlogCount, color: 'text-gray-500' },
    ...(conceptCount > 0 ? [{ label: 'Concept', value: conceptCount, color: 'text-indigo-600 dark:text-indigo-400' }] : []),
    ...(holdCount > 0 ? [{ label: 'On Hold', value: holdCount, color: 'text-red-600 dark:text-red-400' }] : []),
    ...(bookCount > 0 ? [{ label: 'Books', value: bookCount, color: 'text-rose-600 dark:text-rose-400' }] : []),
    ...(blogCount > 0 ? [{ label: 'Blog Posts', value: blogCount, color: 'text-violet-600 dark:text-violet-400' }] : []),
    { label: 'Pipeline', value: `${pipelinePct}%`, color: 'text-blue-600 dark:text-blue-400', bar: pipelinePct, barColor: 'bg-blue-500' },
    { label: 'Cross-Corr', value: `${xcPct}%`, color: xcPct > 70 ? 'text-green-600 dark:text-green-400' : xcPct > 30 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400', bar: xcPct, barColor: xcPct > 70 ? 'bg-green-500' : xcPct > 30 ? 'bg-amber-500' : 'bg-red-500' },
  ]

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-3">
      {stats.map(s => (
        <div key={s.label} className="glass-card p-3">
          <div className={`text-2xl font-mono font-bold ${s.color}`}>{s.value}</div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mt-0.5">{s.label}</div>
          {'bar' in s && s.bar !== undefined && (
            <div className="h-1 bg-gray-200 dark:bg-gray-700 mt-1.5">
              <div className={`h-full ${s.barColor} transition-all`} style={{ width: `${s.bar}%` }} />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ─── Continue Prompt Modal ───
function ContinuePromptModal({ docId, onClose }: { docId: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  const { data, isLoading } = useQuery({
    queryKey: ['continue-prompt', docId],
    queryFn: async () => {
      const resp = await fetch(`/api/tracker/documents/${docId}/continue-prompt`)
      if (!resp.ok) throw new Error('Failed to generate prompt')
      return resp.json() as Promise<{ prompt: string; nextStage: string; documentTitle: string }>
    },
  })

  const copyPrompt = useCallback(() => {
    if (data?.prompt) {
      navigator.clipboard.writeText(data.prompt)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [data])

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-surface-800 w-full max-w-2xl max-h-[80vh] flex flex-col shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white">Continue Prompt</h3>
            {data && (
              <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                Next stage: <span className="font-mono font-medium">{data.nextStage}</span> — {data.documentTitle}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={copyPrompt}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors"
              disabled={!data}
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex justify-center py-8"><KaijuLoader size="sm" /></div>
          ) : data ? (
            <pre className="text-xs font-mono whitespace-pre-wrap text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-surface-900 p-4 border border-gray-200 dark:border-gray-700">
              {data.prompt}
            </pre>
          ) : (
            <p className="text-sm text-red-500">Failed to generate prompt.</p>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Marketing Submission Modal ───
function MarketingSubmissionModal({ docId, onClose }: { docId: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  const { data, isLoading, error } = useQuery({
    queryKey: ['marketing-submission', docId],
    queryFn: async () => {
      const resp = await fetch(`/api/tracker/documents/${docId}/marketing-submission`)
      if (!resp.ok) throw new Error('Marketing submission not found')
      return resp.json() as Promise<{ content: string; exists: boolean; documentTitle: string }>
    },
  })

  const copyContent = useCallback(() => {
    if (data?.content) {
      navigator.clipboard.writeText(data.content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [data])

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-surface-800 w-full max-w-3xl max-h-[85vh] flex flex-col shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white">Marketing Submission Form</h3>
            {data && (
              <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                {data.documentTitle}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={copyContent}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors"
              disabled={!data}
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex justify-center py-8"><KaijuLoader size="sm" /></div>
          ) : error ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Marketing submission form not yet generated. Run <code className="font-mono text-xs bg-gray-100 dark:bg-surface-900 px-1 py-0.5">/marketing-submission {docId}</code> to create it.</p>
          ) : data ? (
            <pre className="text-xs font-mono whitespace-pre-wrap text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-surface-900 p-4 border border-gray-200 dark:border-gray-700">
              {data.content}
            </pre>
          ) : null}
        </div>
      </div>
    </div>
  )
}

// ─── Add Item Dialog ───
function AddItemDialog({ onClose, queryClient }: { onClose: () => void; queryClient: ReturnType<typeof useQueryClient> }) {
  const [id, setId] = useState('')
  const [title, setTitle] = useState('')
  const [shortTitle, setShortTitle] = useState('')
  const [coreIdea, setCoreIdea] = useState('')
  const [seriesGroup, setSeriesGroup] = useState('standalone-wp')
  const [tags, setTags] = useState('')

  const addMutation = useMutation({
    mutationFn: async () => {
      const resp = await fetch('/api/tracker/documents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id,
          title,
          shortTitle,
          coreIdea: coreIdea || undefined,
          seriesGroup,
          tags: tags ? tags.split(',').map(t => t.trim()) : [],
        }),
      })
      if (!resp.ok) {
        const err = await resp.json()
        throw new Error(err.error || 'Failed to add item')
      }
      return resp.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracker'] })
      onClose()
    },
  })

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-surface-800 w-full max-w-lg shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white">Add Punch List Item</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">ID</label>
            <input value={id} onChange={e => setId(e.target.value)} placeholder="WP-IDEA-27 or P-56"
              className="w-full text-xs bg-white dark:bg-surface-900 border border-gray-200 dark:border-gray-700 px-2.5 py-1.5 text-gray-700 dark:text-gray-300" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Full working title"
              className="w-full text-xs bg-white dark:bg-surface-900 border border-gray-200 dark:border-gray-700 px-2.5 py-1.5 text-gray-700 dark:text-gray-300" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Short Title</label>
            <input value={shortTitle} onChange={e => setShortTitle(e.target.value)} placeholder="Brief label"
              className="w-full text-xs bg-white dark:bg-surface-900 border border-gray-200 dark:border-gray-700 px-2.5 py-1.5 text-gray-700 dark:text-gray-300" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Core Idea (optional)</label>
            <textarea value={coreIdea} onChange={e => setCoreIdea(e.target.value)} rows={2} placeholder="One-paragraph description"
              className="w-full text-xs bg-white dark:bg-surface-900 border border-gray-200 dark:border-gray-700 px-2.5 py-1.5 text-gray-700 dark:text-gray-300" />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Group</label>
              <select value={seriesGroup} onChange={e => setSeriesGroup(e.target.value)}
                className="w-full text-xs bg-white dark:bg-surface-900 border border-gray-200 dark:border-gray-700 px-2.5 py-1.5 text-gray-700 dark:text-gray-300">
                {GROUP_ORDER.map(g => <option key={g} value={g}>{GROUP_LABELS[g]}</option>)}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Tags (comma-separated)</label>
              <input value={tags} onChange={e => setTags(e.target.value)} placeholder="tag1, tag2"
                className="w-full text-xs bg-white dark:bg-surface-900 border border-gray-200 dark:border-gray-700 px-2.5 py-1.5 text-gray-700 dark:text-gray-300" />
            </div>
          </div>
          {addMutation.isError && (
            <p className="text-xs text-red-500">{(addMutation.error as Error).message}</p>
          )}
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
            Cancel
          </button>
          <button
            onClick={() => addMutation.mutate()}
            disabled={!id || !title || !shortTitle || addMutation.isPending}
            className="px-3 py-1.5 text-xs font-medium bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50 transition-colors"
          >
            {addMutation.isPending ? 'Adding...' : 'Add Item'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Punch List Section ───
function PunchListSection({ data, queryClient }: { data: TrackerData; queryClient: ReturnType<typeof useQueryClient> }) {
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [promptDocId, setPromptDocId] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  const holdMutation = useMutation({
    mutationFn: async (id: string) => {
      const resp = await fetch(`/api/tracker/documents/${id}/hold`, { method: 'POST' })
      if (!resp.ok) throw new Error('Failed')
      return resp.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tracker'] }),
  })

  const convertMutation = useMutation({
    mutationFn: async (id: string) => {
      const resp = await fetch(`/api/tracker/documents/${id}/convert`, { method: 'POST' })
      if (!resp.ok) throw new Error('Failed')
      return resp.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tracker'] }),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const resp = await fetch(`/api/tracker/documents/${id}`, { method: 'DELETE' })
      if (!resp.ok) throw new Error('Failed')
      return resp.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracker'] })
      setConfirmDelete(null)
    },
  })

  const resumeMutation = useMutation({
    mutationFn: async (id: string) => {
      const resp = await fetch(`/api/tracker/documents/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'backlog', lastUpdated: new Date().toISOString().split('T')[0] }),
      })
      if (!resp.ok) throw new Error('Failed')
      return resp.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tracker'] }),
  })

  // Sort docs: in-progress first, then backlog, then hold, then final
  const sortedDocs = useMemo(() => {
    const order: Record<string, number> = {
      'white-paper-only': 0, outlined: 1, partial: 2, backlog: 3, hold: 4, final: 5,
    }
    return [...data.documents].sort((a, b) => (order[a.status] ?? 3) - (order[b.status] ?? 3))
  }, [data.documents])

  const nextStageLabel = (doc: TrackerDocument): string => {
    const stages = data.meta.pipelineStages
    const labels = data.meta.stageLabels
    for (const s of stages) {
      if (!doc.pipeline[s]?.done) return labels[s] || s
    }
    return 'Complete'
  }

  const completionPct = (doc: TrackerDocument): number => {
    const stages = data.meta.pipelineStages
    const done = stages.filter(s => doc.pipeline[s]?.done).length
    return Math.round((done / stages.length) * 100)
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Punch List</h2>
        <button
          onClick={() => setShowAddDialog(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Item
        </button>
      </div>

      <div className="space-y-2">
        {sortedDocs.map(doc => {
          const pct = completionPct(doc)
          const next = nextStageLabel(doc)
          const isHold = doc.status === 'hold'
          const isFinal = doc.status === 'final'
          const needsConvert = doc.status === 'backlog' || doc.status === 'white-paper-only'

          return (
            <div
              key={doc.id}
              className={`glass-card p-3 transition-all ${isHold ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start gap-3">
                {/* Progress indicator */}
                <div className="flex-shrink-0 w-12 h-12 relative">
                  <svg viewBox="0 0 36 36" className="w-full h-full">
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      className="stroke-gray-200 dark:stroke-gray-700"
                      strokeWidth="3"
                    />
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      className={isFinal ? 'stroke-green-500' : isHold ? 'stroke-red-400' : 'stroke-primary-500'}
                      strokeWidth="3"
                      strokeDasharray={`${pct}, 100`}
                      strokeLinecap="butt"
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-[9px] font-mono font-bold text-gray-600 dark:text-gray-400">
                    {pct}%
                  </span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-mono text-xs font-bold text-gray-500 dark:text-gray-400">{doc.id}</span>
                    <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${STATUS_STYLES[doc.status] || STATUS_STYLES.backlog}`}>
                      {doc.status.replace(/-/g, ' ')}
                    </span>
                    <span className="text-[9px] text-gray-400 dark:text-gray-500">{GROUP_LABELS[doc.seriesGroup]?.split('(')[0]?.trim()}</span>
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">{doc.title}</h3>
                  {!isFinal && (
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                      Next: <span className="font-medium">{next}</span>
                      {doc.lastUpdated && <> — Last updated: {doc.lastUpdated}</>}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Continue prompt */}
                  <button
                    onClick={() => setPromptDocId(doc.id)}
                    className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                    title="Generate continue prompt"
                  >
                    <Sparkles className="w-3 h-3" />
                    Continue
                  </button>

                  {/* Convert to working idea */}
                  {needsConvert && (
                    <button
                      onClick={() => convertMutation.mutate(doc.id)}
                      disabled={convertMutation.isPending}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                      title="Convert to working idea"
                    >
                      <ArrowRight className="w-3 h-3" />
                      Convert
                    </button>
                  )}

                  {/* Hold / Resume */}
                  {!isFinal && !isHold && (
                    <button
                      onClick={() => holdMutation.mutate(doc.id)}
                      disabled={holdMutation.isPending}
                      className="p-1 text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                      title="Place on hold"
                    >
                      <Pause className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {isHold && (
                    <button
                      onClick={() => resumeMutation.mutate(doc.id)}
                      disabled={resumeMutation.isPending}
                      className="p-1 text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                      title="Resume"
                    >
                      <Play className="w-3.5 h-3.5" />
                    </button>
                  )}

                  {/* Delete */}
                  {confirmDelete === doc.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => deleteMutation.mutate(doc.id)}
                        className="px-1.5 py-0.5 text-[9px] font-bold bg-red-500 text-white hover:bg-red-600"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setConfirmDelete(null)}
                        className="px-1.5 py-0.5 text-[9px] text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDelete(doc.id)}
                      className="p-1 text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Progress bar */}
              <div className="mt-2 h-1 bg-gray-200 dark:bg-gray-700">
                <div
                  className={`h-full transition-all ${isFinal ? 'bg-green-500' : isHold ? 'bg-red-400' : 'bg-primary-500'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>

      {showAddDialog && <AddItemDialog onClose={() => setShowAddDialog(false)} queryClient={queryClient} />}
      {promptDocId && <ContinuePromptModal docId={promptDocId} onClose={() => setPromptDocId(null)} />}
    </div>
  )
}

// ─── Pipeline Section ───
function PipelineSection({
  data, filterStatus, setFilterStatus, filterGroup, setFilterGroup,
  filterType, setFilterType,
  searchTerm, setSearchTerm, expandedIds, setExpandedIds,
}: {
  data: TrackerData
  filterStatus: string; setFilterStatus: (v: string) => void
  filterGroup: string; setFilterGroup: (v: string) => void
  filterType: string; setFilterType: (v: string) => void
  searchTerm: string; setSearchTerm: (v: string) => void
  expandedIds: Set<string>; setExpandedIds: (v: Set<string> | ((prev: Set<string>) => Set<string>)) => void
}) {
  const queryClient = useQueryClient()
  const [promptDocId, setPromptDocId] = useState<string | null>(null)
  const [marketingDocId, setMarketingDocId] = useState<string | null>(null)
  const [rerunDocId, setRerunDocId] = useState<string | null>(null)
  const stages = data.meta.pipelineStages
  const labels = data.meta.stageLabels

  const togglePublishedMutation = useMutation({
    mutationFn: async ({ id, published }: { id: string; published: boolean }) => {
      const resp = await fetch(`/api/tracker/documents/${id}/published`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ published }),
      })
      if (!resp.ok) {
        const err = await resp.json()
        throw new Error(err.error || 'Failed to toggle published')
      }
      return resp.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tracker'] }),
  })

  const onTogglePublished = useCallback((id: string, published: boolean) => {
    togglePublishedMutation.mutate({ id, published })
  }, [togglePublishedMutation])

  const toggleLinkedinMutation = useMutation({
    mutationFn: async ({ id, published }: { id: string; published: boolean }) => {
      const resp = await fetch(`/api/tracker/documents/${id}/linkedin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ published }),
      })
      if (!resp.ok) {
        const err = await resp.json()
        throw new Error(err.error || 'Failed to toggle LinkedIn published')
      }
      return resp.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tracker'] }),
  })

  const onToggleLinkedin = useCallback((id: string, published: boolean) => {
    toggleLinkedinMutation.mutate({ id, published })
  }, [toggleLinkedinMutation])

  const onRerun = useCallback((id: string) => {
    setRerunDocId(id)
  }, [])

  const openFolderMutation = useMutation({
    mutationFn: async (id: string) => {
      const resp = await fetch(`/api/tracker/documents/${id}/open-folder`, { method: 'POST' })
      if (!resp.ok) {
        const err = await resp.json()
        throw new Error(err.error || 'Failed to open folder')
      }
      return resp.json()
    },
  })

  const generateSummaryMutation = useMutation({
    mutationFn: async (id: string) => {
      const resp = await fetch(`/api/tracker/documents/${id}/generate-summary`, { method: 'POST' })
      if (!resp.ok) {
        const err = await resp.json()
        throw new Error(err.error || 'Failed to generate summary')
      }
      return resp.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tracker'] }),
  })

  const onOpenFolder = useCallback((id: string) => {
    openFolderMutation.mutate(id)
  }, [openFolderMutation])

  const onGenerateSummary = useCallback((id: string) => {
    generateSummaryMutation.mutate(id)
  }, [generateSummaryMutation])

  const filteredDocs = useMemo(() => {
    let docs = data.documents
    if (filterStatus !== 'all') {
      if (filterStatus === 'partial') {
        docs = docs.filter(d => ['partial', 'outlined', 'white-paper-only'].includes(d.status))
      } else {
        docs = docs.filter(d => d.status === filterStatus)
      }
    }
    if (filterGroup !== 'all') {
      docs = docs.filter(d => d.seriesGroup === filterGroup)
    }
    if (filterType !== 'all') {
      docs = docs.filter(d => d.type === filterType)
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      docs = docs.filter(d =>
        d.title.toLowerCase().includes(term) ||
        d.shortTitle.toLowerCase().includes(term) ||
        d.id.toLowerCase().includes(term)
      )
    }
    return docs
  }, [data.documents, filterStatus, filterGroup, filterType, searchTerm])

  const wpDocs = useMemo(() => filteredDocs.filter(d => d.type !== 'blog' && d.type !== 'book'), [filteredDocs])
  const blogDocs = useMemo(() => filteredDocs.filter(d => d.type === 'blog'), [filteredDocs])
  const bookDocs = useMemo(() => {
    const books = filteredDocs.filter(d => d.type === 'book')
    return books.sort((a, b) => BOOK_ORDER.indexOf(a.id) - BOOK_ORDER.indexOf(b.id))
  }, [filteredDocs])

  const grouped = useMemo(() => {
    const g: Record<string, TrackerDocument[]> = {}
    wpDocs.forEach(d => {
      if (!g[d.seriesGroup]) g[d.seriesGroup] = []
      g[d.seriesGroup]!.push(d)
    })
    return g
  }, [wpDocs])

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev: Set<string>) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [setExpandedIds])

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-3 items-center">
        <div className="flex items-center gap-1.5">
          <Filter className="w-3.5 h-3.5 text-gray-400" />
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="text-xs bg-white dark:bg-surface-800 border border-gray-200 dark:border-gray-700 px-2 py-1.5 text-gray-700 dark:text-gray-300"
          >
            <option value="all">All Status</option>
            <option value="final">Final</option>
            <option value="partial">In Progress</option>
            <option value="outlined">Outlined</option>
            <option value="concept">Concept</option>
            <option value="white-paper-only">White Paper Only</option>
            <option value="backlog">Backlog</option>
            <option value="hold">On Hold</option>
          </select>
        </div>
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          className="text-xs bg-white dark:bg-surface-800 border border-gray-200 dark:border-gray-700 px-2 py-1.5 text-gray-700 dark:text-gray-300"
        >
          <option value="all">All Types</option>
          <option value="wp-idea">White Papers</option>
          <option value="book">Books</option>
          <option value="blog">Blogs</option>
          <option value="pattern">Patterns</option>
          <option value="field-manual">Field Manuals</option>
        </select>
        <select
          value={filterGroup}
          onChange={e => setFilterGroup(e.target.value)}
          className="text-xs bg-white dark:bg-surface-800 border border-gray-200 dark:border-gray-700 px-2 py-1.5 text-gray-700 dark:text-gray-300"
        >
          <option value="all">All Groups</option>
          {GROUP_ORDER.map(g => (
            <option key={g} value={g}>{GROUP_LABELS[g]}</option>
          ))}
        </select>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search titles..."
            className="text-xs bg-white dark:bg-surface-800 border border-gray-200 dark:border-gray-700 pl-7 pr-2 py-1.5 text-gray-700 dark:text-gray-300 w-48"
          />
        </div>
      </div>

      {/* Whitepaper Pipeline Table */}
      {wpDocs.length > 0 && (
        <div className="glass-card overflow-x-auto" style={{ padding: 0 }}>
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-surface-800 border-b border-gray-200 dark:border-gray-700">
                <th className="text-center px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px] w-8" title="Human-gated Published flag">PUB</th>
                <th className="text-center px-2 py-2 font-semibold text-blue-400 dark:text-blue-500 uppercase tracking-wider text-[10px] w-8" title="Published to LinkedIn">LI</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px] w-6"></th>
                <th className="text-left px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">ID</th>
                <th className="text-left px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">Title</th>
                <th className="text-left px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">Pop Culture</th>
                <th className="text-left px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">Historical</th>
                <th className="text-center px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">Step</th>
                <th className="text-left px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">Stage</th>
                <th className="text-center px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">Stage Date</th>
                <th className="text-center px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">Updated</th>
                <th className="text-center px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">Status</th>
                <th className="text-center px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px] w-16"></th>
              </tr>
            </thead>
            <tbody>
              {GROUP_ORDER.filter(g => g !== 'geek-translation').map(g => {
                if (!grouped[g]) return null
                return (
                  <GroupRows
                    key={g}
                    group={g}
                    docs={grouped[g]}
                    stages={stages}
                    labels={labels}
                    expandedIds={expandedIds}
                    toggleExpand={toggleExpand}
                    interrelations={data.interrelations}
                    showGroupHeader={filterGroup === 'all'}
                    onContinuePrompt={setPromptDocId}
                    onOpenFolder={onOpenFolder}
                    onGenerateSummary={onGenerateSummary}
                    onMarketingSubmission={setMarketingDocId}
                    onTogglePublished={onTogglePublished}
                    onToggleLinkedin={onToggleLinkedin}
                    onRerun={onRerun}
                  />
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Book Series Table */}
      {bookDocs.length > 0 && (
        <BookPipelineTable
          docs={bookDocs}
          bookStages={data.meta.bookPipelineStages || []}
          labels={labels}
          expandedIds={expandedIds}
          toggleExpand={toggleExpand}
          onContinuePrompt={setPromptDocId}
          onOpenFolder={onOpenFolder}
        />
      )}

      {/* Blog Series Table */}
      {blogDocs.length > 0 && (
        <BlogPipelineTable
          docs={blogDocs}
          blogStages={data.meta.blogPipelineStages || []}
          labels={labels}
          expandedIds={expandedIds}
          toggleExpand={toggleExpand}
          onContinuePrompt={setPromptDocId}
          onOpenFolder={onOpenFolder}
        />
      )}

      {filteredDocs.length === 0 && (
        <div className="glass-card text-center py-8 text-sm text-gray-400 dark:text-gray-500">
          No documents match the current filters.
        </div>
      )}

      {promptDocId && <ContinuePromptModal docId={promptDocId} onClose={() => setPromptDocId(null)} />}
      {marketingDocId && <MarketingSubmissionModal docId={marketingDocId} onClose={() => setMarketingDocId(null)} />}
      {rerunDocId && (
        <RerunFlowModal
          docId={rerunDocId}
          stages={stages}
          labels={labels}
          onClose={() => setRerunDocId(null)}
          onDone={() => queryClient.invalidateQueries({ queryKey: ['tracker'] })}
        />
      )}
    </div>
  )
}

// ─── Blog Pipeline Table ───
function BlogPipelineTable({
  docs, blogStages, labels, expandedIds, toggleExpand, onContinuePrompt, onOpenFolder,
}: {
  docs: TrackerDocument[]; blogStages: string[]
  labels: Record<string, string>
  expandedIds: Set<string>; toggleExpand: (id: string) => void
  onContinuePrompt: (id: string) => void
  onOpenFolder: (id: string) => void
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2 mt-4">
        <div className="w-1.5 h-6 bg-violet-500 dark:bg-violet-400 rounded-full" />
        <h3 className="text-sm font-bold text-gray-900 dark:text-white">The Geek Translation: Culture Meets Code</h3>
        <span className="text-[10px] px-2 py-0.5 bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400 font-medium">
          Blog + Podcast Series
        </span>
        <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-auto">
          {docs.filter(d => {
            const total = blogStages.length
            const done = blogStages.filter(s => d.pipeline[s]?.done).length
            return done === total
          }).length}/{docs.length} published
        </span>
      </div>
      <div className="glass-card overflow-x-auto" style={{ padding: 0 }}>
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-violet-50 dark:bg-violet-900/20 border-b border-gray-200 dark:border-gray-700">
              <th className="text-left px-3 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px] w-6"></th>
              <th className="text-left px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">ID</th>
              <th className="text-left px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">Title</th>
              <th className="text-left px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">Pop Culture</th>
              <th className="text-left px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">Historical</th>
              <th className="text-center px-2 py-2 font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wider text-[10px]">Step</th>
              <th className="text-left px-2 py-2 font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wider text-[10px]">Stage</th>
              <th className="text-center px-2 py-2 font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wider text-[10px]">Stage Date</th>
              <th className="text-center px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">Updated</th>
              <th className="text-center px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">Status</th>
              <th className="text-center px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px] w-16"></th>
            </tr>
          </thead>
          <tbody>
            {docs.map(doc => (
              <BlogDocumentRow
                key={doc.id}
                doc={doc}
                blogStages={blogStages}
                labels={labels}
                isExpanded={expandedIds.has(doc.id)}
                toggleExpand={toggleExpand}
                onContinuePrompt={onContinuePrompt}
                onOpenFolder={onOpenFolder}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function BlogDocumentRow({
  doc, blogStages, labels, isExpanded, toggleExpand, onContinuePrompt, onOpenFolder,
}: {
  doc: TrackerDocument; blogStages: string[]; labels: Record<string, string>
  isExpanded: boolean; toggleExpand: (id: string) => void
  onContinuePrompt: (id: string) => void
  onOpenFolder: (id: string) => void
}) {
  const popCulture = doc.popCulture || ''
  const donePct = blogStages.length > 0 ? Math.round((blogStages.filter(s => doc.pipeline[s]?.done).length / blogStages.length) * 100) : 0

  return (
    <>
      <tr
        className="border-b border-gray-100 dark:border-gray-800 hover:bg-violet-50/50 dark:hover:bg-violet-900/10 cursor-pointer transition-colors"
        onClick={() => toggleExpand(doc.id)}
      >
        <td className="px-3 py-1.5 text-gray-400">
          {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </td>
        <td className="px-2 py-1.5 font-mono font-medium text-violet-700 dark:text-violet-400 whitespace-nowrap">
          {doc.id}
        </td>
        <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300 max-w-[200px] truncate" title={doc.title}>
          {doc.shortTitle}
        </td>
        <td className="px-2 py-1.5 text-amber-700 dark:text-amber-400 text-[10px] whitespace-nowrap" title={doc.popCultureNote || ''}>
          {popCulture}
        </td>
        <td className="px-2 py-1.5 text-cyan-700 dark:text-cyan-400 text-[10px] whitespace-nowrap" title={doc.historicalRefNote || ''}>
          {doc.historicalRef || ''}
        </td>
        {(() => {
          const cur = getCurrentStage(doc.pipeline, blogStages, labels)
          const isComplete = cur.step === cur.total
          return (
            <>
              <td className={`px-2 py-1.5 text-center font-mono text-[10px] font-bold ${isComplete ? 'text-green-600 dark:text-green-400' : 'text-violet-600 dark:text-violet-400'}`}>
                {cur.step}/{cur.total}
              </td>
              <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300 text-[10px] max-w-[140px] truncate" title={cur.name}>
                {cur.step === 0 ? <span className="text-gray-400 italic">Not started</span> : cur.name}
              </td>
              <td className="px-2 py-1.5 text-center font-mono text-[10px] text-gray-400">{cur.date || '—'}</td>
            </>
          )
        })()}
        <td className="px-2 py-1.5 text-center font-mono text-[10px] text-gray-400">{doc.lastUpdated || '\u2014'}</td>
        <td className="px-2 py-1.5 text-center">
          <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${STATUS_STYLES[doc.status] || STATUS_STYLES.backlog}`}>
            {doc.status.replace(/-/g, ' ')}
          </span>
        </td>
        <td className="px-2 py-1.5 text-center" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-center gap-0.5">
            {doc.folder && (
              <button
                onClick={() => onOpenFolder(doc.id)}
                className="p-1 text-gray-400 hover:text-violet-500 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
                title="Open folder"
              >
                <FolderOpen className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={() => onContinuePrompt(doc.id)}
              className="p-1 text-violet-500 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
              title="Generate continue prompt"
            >
              <Sparkles className="w-3.5 h-3.5" />
            </button>
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr className="border-b border-gray-200 dark:border-gray-700">
          <td colSpan={11} className="px-6 py-4 bg-violet-50/30 dark:bg-violet-900/10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div>
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-violet-600 dark:text-violet-400 mb-2">Blog Pipeline</h4>
                <ul className="space-y-0.5">
                  {blogStages.map((s, idx) => {
                    const stage = doc.pipeline[s]
                    return (
                      <li key={s} className="flex items-center gap-1.5">
                        {stage?.done ? (
                          <span className="w-2.5 h-2.5 bg-violet-500 dark:bg-violet-400 inline-block" />
                        ) : (
                          <span className="w-2.5 h-2.5 border border-violet-300 dark:border-violet-600 inline-block" />
                        )}
                        <span className="font-mono text-[10px] text-gray-400 w-4 text-right">{idx + 1}.</span>
                        <span className={stage?.done ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}>
                          {labels[s] || s}
                        </span>
                        {stage?.date && <span className="text-[10px] text-gray-400 ml-1">({stage.date})</span>}
                      </li>
                    )
                  })}
                </ul>
                <div className="mt-2 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-violet-500 transition-all" style={{ width: `${donePct}%` }} />
                </div>
                <span className="text-[10px] text-gray-400 mt-0.5">{donePct}% complete</span>
              </div>
              <div>
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Summary</h4>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{doc.summary || 'No summary available.'}</p>
                {doc.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {doc.tags.filter(t => t !== 'blog' && t !== 'geek-translation').map(t => (
                      <span key={t} className="px-1.5 py-0.5 text-[9px] bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 font-medium">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// ─── Book Pipeline Table ───
function BookPipelineTable({
  docs, bookStages, labels, expandedIds, toggleExpand, onContinuePrompt, onOpenFolder,
}: {
  docs: TrackerDocument[]; bookStages: string[]
  labels: Record<string, string>
  expandedIds: Set<string>; toggleExpand: (id: string) => void
  onContinuePrompt: (id: string) => void
  onOpenFolder: (id: string) => void
}) {
  const completedCount = docs.filter(d => {
    const done = bookStages.filter(s => d.pipeline[s]?.done).length
    return done === bookStages.length
  }).length

  return (
    <div>
      <div className="flex items-center gap-2 mb-2 mt-4">
        <div className="w-1.5 h-6 bg-rose-500 dark:bg-rose-400 rounded-full" />
        <BookOpen className="w-4 h-4 text-rose-600 dark:text-rose-400" />
        <h3 className="text-sm font-bold text-gray-900 dark:text-white">Three-Body Problem Book Series</h3>
        <span className="text-[10px] px-2 py-0.5 bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400 font-medium">
          7 Volumes
        </span>
        <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-auto">
          {completedCount}/{docs.length} complete
        </span>
      </div>
      <div className="glass-card overflow-x-auto" style={{ padding: 0 }}>
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-rose-50 dark:bg-rose-900/20 border-b border-gray-200 dark:border-gray-700">
              <th className="text-left px-3 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px] w-6"></th>
              <th className="text-left px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">ID</th>
              <th className="text-left px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">Volume</th>
              <th className="text-left px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">Papers</th>
              <th className="text-center px-2 py-2 font-semibold text-rose-600 dark:text-rose-400 uppercase tracking-wider text-[10px]">Step</th>
              <th className="text-left px-2 py-2 font-semibold text-rose-600 dark:text-rose-400 uppercase tracking-wider text-[10px]">Stage</th>
              <th className="text-center px-2 py-2 font-semibold text-rose-600 dark:text-rose-400 uppercase tracking-wider text-[10px]">Stage Date</th>
              <th className="text-center px-2 py-2 font-semibold text-rose-600 dark:text-rose-400 uppercase tracking-wider text-[10px]">Version</th>
              <th className="text-center px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">Updated</th>
              <th className="text-center px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">Status</th>
              <th className="text-center px-2 py-2 font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px] w-16"></th>
            </tr>
          </thead>
          <tbody>
            {docs.map(doc => (
              <BookDocumentRow
                key={doc.id}
                doc={doc}
                bookStages={bookStages}
                labels={labels}
                isExpanded={expandedIds.has(doc.id)}
                toggleExpand={toggleExpand}
                onContinuePrompt={onContinuePrompt}
                onOpenFolder={onOpenFolder}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function BookDocumentRow({
  doc, bookStages, labels, isExpanded, toggleExpand, onContinuePrompt, onOpenFolder,
}: {
  doc: TrackerDocument; bookStages: string[]; labels: Record<string, string>
  isExpanded: boolean; toggleExpand: (id: string) => void
  onContinuePrompt: (id: string) => void
  onOpenFolder: (id: string) => void
}) {
  const donePct = bookStages.length > 0 ? Math.round((bookStages.filter(s => doc.pipeline[s]?.done).length / bookStages.length) * 100) : 0
  const chapterCount = doc.pipeline.chapters_split?.file || ''

  return (
    <>
      <tr
        className="border-b border-gray-100 dark:border-gray-800 hover:bg-rose-50/50 dark:hover:bg-rose-900/10 cursor-pointer transition-colors"
        onClick={() => toggleExpand(doc.id)}
      >
        <td className="px-3 py-1.5 text-gray-400">
          {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </td>
        <td className="px-2 py-1.5 font-mono font-medium text-rose-700 dark:text-rose-400 whitespace-nowrap">
          {doc.id}
        </td>
        <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300 max-w-[250px] truncate" title={doc.title}>
          {BOOK_LABELS[doc.id] || doc.shortTitle}
        </td>
        <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400 text-[10px] whitespace-nowrap font-mono">
          {chapterCount}
        </td>
        {(() => {
          const cur = getCurrentStage(doc.pipeline, bookStages, labels)
          const isComplete = cur.step === cur.total
          return (
            <>
              <td className={`px-2 py-1.5 text-center font-mono text-[10px] font-bold ${isComplete ? 'text-green-600 dark:text-green-400' : 'text-rose-600 dark:text-rose-400'}`}>
                {cur.step}/{cur.total}
              </td>
              <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300 text-[10px] max-w-[140px] truncate" title={cur.name}>
                {cur.step === 0 ? <span className="text-gray-400 italic">Not started</span> : cur.name}
              </td>
              <td className="px-2 py-1.5 text-center font-mono text-[10px] text-gray-400">{cur.date || '—'}</td>
            </>
          )
        })()}
        <td className="px-2 py-1.5 text-center font-mono text-[10px] text-rose-600 dark:text-rose-400">
          {doc.version ? `v${doc.version}` : '—'}
        </td>
        <td className="px-2 py-1.5 text-center font-mono text-[10px] text-gray-400">{doc.lastUpdated || '\u2014'}</td>
        <td className="px-2 py-1.5 text-center">
          <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${STATUS_STYLES[doc.status] || STATUS_STYLES.backlog}`}>
            {doc.status.replace(/-/g, ' ')}
          </span>
        </td>
        <td className="px-2 py-1.5 text-center" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-center gap-0.5">
            {doc.folder && (
              <button
                onClick={() => onOpenFolder(doc.id)}
                className="p-1 text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
                title="Open folder"
              >
                <FolderOpen className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={() => onContinuePrompt(doc.id)}
              className="p-1 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
              title="Generate continue prompt"
            >
              <Sparkles className="w-3.5 h-3.5" />
            </button>
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr className="border-b border-gray-200 dark:border-gray-700">
          <td colSpan={11} className="px-6 py-4 bg-rose-50/30 dark:bg-rose-900/10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div>
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-rose-600 dark:text-rose-400 mb-2">Book Pipeline</h4>
                <ul className="space-y-0.5">
                  {bookStages.map((s, idx) => {
                    const stage = doc.pipeline[s]
                    return (
                      <li key={s} className="flex items-center gap-1.5">
                        {stage?.done ? (
                          <span className="w-2.5 h-2.5 bg-rose-500 dark:bg-rose-400 inline-block" />
                        ) : (
                          <span className="w-2.5 h-2.5 border border-rose-300 dark:border-rose-600 inline-block" />
                        )}
                        <span className="font-mono text-[10px] text-gray-400 w-5 text-right">{idx + 1}.</span>
                        <span className={stage?.done ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}>
                          {labels[s] || s}
                        </span>
                        {stage?.file && <span className="text-gray-400 font-mono">- {stage.file}</span>}
                        {stage?.date && <span className="text-[10px] text-gray-400 ml-1">({stage.date})</span>}
                      </li>
                    )
                  })}
                </ul>
                <div className="mt-2 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-rose-500 transition-all" style={{ width: `${donePct}%` }} />
                </div>
                <span className="text-[10px] text-gray-400 mt-0.5">{donePct}% complete</span>
              </div>
              <div>
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Details</h4>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">{doc.title}</p>
                {doc.summary && (
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-2">{doc.summary}</p>
                )}
                {doc.folder && (
                  <div className="mb-2">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Location</h4>
                    <div className="flex items-center gap-1 bg-gray-100 dark:bg-surface-900 border border-gray-200 dark:border-gray-700 px-2 py-1.5 font-mono text-[10px] text-gray-600 dark:text-gray-400">
                      <span className="flex-1 truncate select-all">{doc.folder}/</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          navigator.clipboard.writeText(`${doc.folder}/`)
                        }}
                        className="flex-shrink-0 p-0.5 text-gray-400 hover:text-rose-500 transition-colors"
                        title="Copy path"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}
                {doc.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {doc.tags.map(t => (
                      <span key={t} className="px-1.5 py-0.5 text-[9px] bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 font-medium">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

function GroupRows({
  group, docs, stages, labels, expandedIds, toggleExpand, interrelations, showGroupHeader, onContinuePrompt, onOpenFolder, onGenerateSummary, onMarketingSubmission, onTogglePublished, onToggleLinkedin, onRerun,
}: {
  group: string; docs: TrackerDocument[]; stages: string[]; labels: Record<string, string>
  expandedIds: Set<string>; toggleExpand: (id: string) => void
  interrelations: Interrelation[]; showGroupHeader: boolean
  onContinuePrompt: (id: string) => void
  onOpenFolder: (id: string) => void
  onGenerateSummary: (id: string) => void
  onMarketingSubmission: (id: string) => void
  onTogglePublished: (id: string, published: boolean) => void
  onToggleLinkedin: (id: string, published: boolean) => void
  onRerun: (id: string) => void
}) {
  return (
    <>
      {showGroupHeader && (
        <tr>
          <td colSpan={13} className="px-3 py-1.5 bg-gray-100 dark:bg-surface-700 text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {GROUP_LABELS[group] || group}
          </td>
        </tr>
      )}
      {docs.map(doc => (
        <DocumentRow
          key={doc.id}
          doc={doc}
          stages={stages}
          labels={labels}
          isExpanded={expandedIds.has(doc.id)}
          toggleExpand={toggleExpand}
          interrelations={interrelations}
          onContinuePrompt={onContinuePrompt}
          onOpenFolder={onOpenFolder}
          onGenerateSummary={onGenerateSummary}
          onMarketingSubmission={onMarketingSubmission}
          onTogglePublished={onTogglePublished}
          onToggleLinkedin={onToggleLinkedin}
          onRerun={onRerun}
        />
      ))}
    </>
  )
}

function DocumentRow({
  doc, stages, labels, isExpanded, toggleExpand, interrelations, onContinuePrompt, onOpenFolder, onGenerateSummary, onMarketingSubmission, onTogglePublished, onToggleLinkedin, onRerun,
}: {
  doc: TrackerDocument; stages: string[]; labels: Record<string, string>
  isExpanded: boolean; toggleExpand: (id: string) => void
  interrelations: Interrelation[]
  onContinuePrompt: (id: string) => void
  onOpenFolder: (id: string) => void
  onGenerateSummary: (id: string) => void
  onMarketingSubmission: (id: string) => void
  onTogglePublished: (id: string, published: boolean) => void
  onToggleLinkedin: (id: string, published: boolean) => void
  onRerun: (id: string) => void
}) {
  const rels = interrelations.filter(r => r.source === doc.id || r.target === doc.id)
  const isPublished = !!doc.humanPublishedAt
  const isLinkedin = !!doc.linkedinPublishedAt

  return (
    <>
      <tr
        className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-surface-800/50 cursor-pointer transition-colors"
        onClick={() => toggleExpand(doc.id)}
      >
        <td className="px-2 py-1.5 text-center" onClick={e => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isPublished}
            onChange={e => onTogglePublished(doc.id, e.target.checked)}
            className="w-3.5 h-3.5 accent-emerald-500 cursor-pointer"
            title={isPublished ? `Published ${doc.humanPublishedAt?.slice(0, 10)}` : 'Not yet published — check when actually published externally'}
          />
        </td>
        <td className="px-2 py-1.5 text-center" onClick={e => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isLinkedin}
            onChange={e => onToggleLinkedin(doc.id, e.target.checked)}
            className="w-3.5 h-3.5 accent-blue-500 cursor-pointer"
            title={isLinkedin ? `LinkedIn article published ${doc.linkedinPublishedAt?.slice(0, 10)}` : 'Not yet published to LinkedIn'}
          />
        </td>
        <td className="px-3 py-1.5 text-gray-400">
          {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </td>
        <td className="px-2 py-1.5 font-mono font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
          {doc.id}
        </td>
        <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300 max-w-[200px] truncate" title={doc.title}>
          {doc.shortTitle}
        </td>
        <td className="px-2 py-1.5 text-amber-700 dark:text-amber-400 text-[10px] max-w-[120px] truncate" title={doc.popCultureNote || doc.popCulture || ''}>
          {doc.popCulture || '—'}
        </td>
        <td className="px-2 py-1.5 text-cyan-700 dark:text-cyan-400 text-[10px] max-w-[120px] truncate" title={doc.historicalRefNote || doc.historicalRef || ''}>
          {doc.historicalRef || '—'}
        </td>
        {(() => {
          const cur = getCurrentStage(doc.pipeline, stages, labels)
          const isComplete = cur.step === cur.total
          return (
            <>
              <td className={`px-2 py-1.5 text-center font-mono text-[10px] font-bold ${isComplete ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                {cur.step}/{cur.total}
              </td>
              <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300 text-[10px] max-w-[140px] truncate" title={cur.name}>
                {cur.step === 0 ? <span className="text-gray-400 italic">Not started</span> : cur.name}
              </td>
              <td className="px-2 py-1.5 text-center font-mono text-[10px] text-gray-400">{cur.date || '—'}</td>
            </>
          )
        })()}
        <td className="px-2 py-1.5 text-center font-mono text-[10px] text-gray-400">{doc.lastUpdated || '—'}</td>
        <td className="px-2 py-1.5 text-center">
          <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${STATUS_STYLES[doc.status] || STATUS_STYLES.backlog}`}>
            {doc.status.replace(/-/g, ' ')}
          </span>
        </td>
        <td className="px-2 py-1.5 text-center" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-center gap-0.5">
            {doc.folder && (
              <button
                onClick={() => onOpenFolder(doc.id)}
                className="p-1 text-gray-400 hover:text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                title="Open folder in Finder"
              >
                <FolderOpen className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={() => onContinuePrompt(doc.id)}
              className="p-1 text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              title="Generate continue prompt"
            >
              <Sparkles className="w-3.5 h-3.5" />
            </button>
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr className="border-b border-gray-200 dark:border-gray-700">
          <td colSpan={12} className="px-6 py-4 bg-gray-50 dark:bg-surface-800/30">
            <div className="mb-3 flex items-center gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); onRerun(doc.id) }}
                className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50 transition-colors"
                title="Reset and rerun a range of pipeline stages"
              >
                Rerun Flow
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onRerun(doc.id) }}
                className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 transition-colors"
                title="Reset all stages and rerun from the beginning"
              >
                Rerun from Start
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div>
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Pipeline Stages</h4>
                <ul className="space-y-0.5">
                  {stages.map((s, idx) => {
                    const stage = doc.pipeline[s]
                    return (
                      <li key={s} className="flex items-center gap-1.5">
                        {stage?.done ? (
                          <span className="w-2.5 h-2.5 bg-green-500 dark:bg-green-400 inline-block" />
                        ) : (
                          <span className="w-2.5 h-2.5 border border-gray-300 dark:border-gray-600 inline-block" />
                        )}
                        <span className="font-mono text-[10px] text-gray-400 w-5 text-right">{idx + 1}.</span>
                        <span className={stage?.done ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}>{labels[s]}</span>
                        {stage?.file && <span className="text-gray-400 font-mono">- {stage.file}</span>}
                        {stage?.date && <span className="text-gray-400">({stage.date})</span>}
                      </li>
                    )
                  })}
                </ul>
              </div>
              <div>
                {doc.summary ? (
                  <div className="mb-3">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Summary</h4>
                    <p className="text-gray-700 dark:text-gray-300 text-xs leading-relaxed">{doc.summary}</p>
                  </div>
                ) : doc.folder && doc.pipeline.draft_paper?.done ? (
                  <div className="mb-3">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Summary</h4>
                    <button
                      onClick={(e) => { e.stopPropagation(); onGenerateSummary(doc.id) }}
                      className="text-[10px] text-primary-500 hover:text-primary-600 dark:hover:text-primary-400 flex items-center gap-1"
                    >
                      <Sparkles className="w-3 h-3" />
                      Generate summary
                    </button>
                  </div>
                ) : null}

                {doc.concept && (
                  <div className="mb-3">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Core Concept</h4>
                    <p className="text-gray-700 dark:text-gray-300 text-xs leading-relaxed">{doc.concept}</p>
                  </div>
                )}

                {doc.popCulture && (
                  <div className="mb-3">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Pop Culture Reference</h4>
                    <p className="text-gray-700 dark:text-gray-300 text-xs"><span className="font-semibold text-amber-700 dark:text-amber-400">{doc.popCulture}</span> — {doc.popCultureNote}</p>
                  </div>
                )}

                {doc.historicalRef && (
                  <div className="mb-3">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Historical Reference</h4>
                    <p className="text-gray-700 dark:text-gray-300 text-xs"><span className="font-semibold text-cyan-700 dark:text-cyan-400">{doc.historicalRef}</span> — {doc.historicalRefNote}</p>
                  </div>
                )}

                <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Full Title</h4>
                <p className="text-gray-700 dark:text-gray-300 mb-2">{doc.title}</p>

                <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">File Location</h4>
                {doc.folder ? (
                  <div className="mb-2">
                    <div className="flex items-center gap-1 bg-gray-100 dark:bg-surface-900 border border-gray-200 dark:border-gray-700 px-2 py-1.5 font-mono text-[10px] text-gray-600 dark:text-gray-400">
                      <span className="flex-1 truncate select-all">documents/library/{doc.folder}/</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          navigator.clipboard.writeText(`documents/library/${doc.folder}/`)
                        }}
                        className="flex-shrink-0 p-0.5 text-gray-400 hover:text-primary-500 transition-colors"
                        title="Copy path"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); onOpenFolder(doc.id) }}
                      className="mt-1 text-[10px] text-primary-500 hover:text-primary-600 dark:hover:text-primary-400 flex items-center gap-1"
                    >
                      <FolderOpen className="w-3 h-3" />
                      Open in Finder
                    </button>
                  </div>
                ) : (
                  <p className="font-mono text-gray-400 text-[10px] mb-2">(no folder assigned)</p>
                )}

                <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Tags</h4>
                <div className="flex flex-wrap gap-1 mb-3">
                  {doc.tags.map(t => (
                    <span key={t} className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-[9px]">{t}</span>
                  ))}
                </div>

                <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Marketing Submission</h4>
                <div className="mb-3">
                  {doc.pipeline.draft_marketing?.done ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); onMarketingSubmission(doc.id) }}
                      className="text-[10px] text-primary-500 hover:text-primary-600 dark:hover:text-primary-400 flex items-center gap-1 font-medium"
                    >
                      <ClipboardList className="w-3 h-3" />
                      View Marketing Form
                    </button>
                  ) : doc.pipeline.draft_paper?.done ? (
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 italic">Not yet generated</span>
                  ) : (
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 italic">Requires full paper first</span>
                  )}
                </div>

                <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                  Interrelations ({rels.length})
                </h4>
                {rels.length > 0 ? (
                  <ul className="space-y-0.5">
                    {rels.map((r, i) => {
                      const direction = r.source === doc.id ? `→ ${r.target}` : `← ${r.source}`
                      return (
                        <li key={i} className="text-gray-600 dark:text-gray-400">
                          <span className="font-mono font-medium">{direction}</span>
                          {' '}<span className="italic">({r.type})</span>: {r.description}
                        </li>
                      )
                    })}
                  </ul>
                ) : (
                  <p className="text-gray-400 italic">No interrelations recorded</p>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// ─── Rerun Flow Modal ───
function RerunFlowModal({
  docId, stages, labels, onClose, onDone,
}: {
  docId: string
  stages: string[]
  labels: Record<string, string>
  onClose: () => void
  onDone: () => void
}) {
  const [fromStage, setFromStage] = useState<string>(stages[0] ?? 'concept')
  const [toStage, setToStage] = useState<string>(stages[stages.length - 1] ?? 'final')
  const [prompt, setPrompt] = useState<string | null>(null)
  const [resetStages, setResetStages] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const submit = async (overrideFrom?: string, overrideTo?: string) => {
    setError(null)
    setSubmitting(true)
    try {
      const resp = await fetch(`/api/tracker/documents/${docId}/rerun`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromStage: overrideFrom ?? fromStage,
          toStage: overrideTo ?? toStage,
        }),
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || data.message || 'Failed to reset stages')
      setPrompt(data.prompt)
      setResetStages(data.resetStages || [])
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSubmitting(false)
    }
  }

  const copyPrompt = () => {
    if (prompt) navigator.clipboard.writeText(prompt)
  }

  const fromIdx = stages.indexOf(fromStage)
  const toIdx = stages.indexOf(toStage)
  const invalidRange = fromIdx < 0 || toIdx < 0 || fromIdx > toIdx

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-surface-900 border border-gray-200 dark:border-gray-700 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-sm font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
            Rerun Flow — {docId}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!prompt ? (
          <div className="px-6 py-4 space-y-4 text-xs">
            <p className="text-gray-600 dark:text-gray-400">
              Pick the range of pipeline stages to reset and rerun. All stages in the selected range
              are cleared to <span className="font-mono">done=false</span>. If the run stops partway
              through, any stages after the stopping point stay undone.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                  Start Stage
                </label>
                <select
                  value={fromStage}
                  onChange={e => setFromStage(e.target.value)}
                  className="w-full bg-white dark:bg-surface-800 border border-gray-200 dark:border-gray-700 px-2 py-1.5 text-gray-700 dark:text-gray-300"
                >
                  {stages.map(s => (
                    <option key={s} value={s}>{labels[s] || s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                  End Stage
                </label>
                <select
                  value={toStage}
                  onChange={e => setToStage(e.target.value)}
                  className="w-full bg-white dark:bg-surface-800 border border-gray-200 dark:border-gray-700 px-2 py-1.5 text-gray-700 dark:text-gray-300"
                >
                  {stages.map(s => (
                    <option key={s} value={s}>{labels[s] || s}</option>
                  ))}
                </select>
              </div>
            </div>

            {invalidRange && (
              <p className="text-red-600 dark:text-red-400 text-[10px]">
                Start stage must come before or equal to end stage.
              </p>
            )}

            {error && <p className="text-red-600 dark:text-red-400 text-[10px]">{error}</p>}

            <div className="flex items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => submit()}
                disabled={submitting || invalidRange}
                className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Resetting...' : 'Reset Range + Build Prompt'}
              </button>
              <button
                onClick={() => submit(stages[0], stages[stages.length - 1])}
                disabled={submitting}
                className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Reset every stage and rerun from the very beginning"
              >
                Rerun from Start
              </button>
              <button
                onClick={onClose}
                className="ml-auto px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="px-6 py-4 space-y-3 text-xs">
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 px-3 py-2 text-emerald-700 dark:text-emerald-300">
              <strong className="font-semibold">{resetStages.length} stages reset.</strong>{' '}
              Paste the prompt below into Claude Code to execute the flow.
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Claude Code Prompt
                </label>
                <button
                  onClick={copyPrompt}
                  className="flex items-center gap-1 text-[10px] text-primary-500 hover:text-primary-600"
                >
                  <Copy className="w-3 h-3" /> Copy
                </button>
              </div>
              <pre className="bg-gray-100 dark:bg-surface-950 border border-gray-200 dark:border-gray-700 p-3 font-mono text-[11px] text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words max-h-[50vh] overflow-y-auto">
                {prompt}
              </pre>
            </div>

            <div className="flex items-center justify-end pt-2 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={onClose}
                className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider bg-primary-500 text-white hover:bg-primary-600"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Finding Card ───
function FindingCard({
  finding,
  selected,
  expanded,
  status,
  onToggleSelect,
  onToggleExpand,
}: {
  finding: AnalysisFinding
  selected: boolean
  expanded: boolean
  status?: 'success' | 'failed'
  onToggleSelect: () => void
  onToggleExpand: () => void
}) {
  const shortPath = (p: string) => {
    const idx = p.indexOf('documents/library/')
    return idx >= 0 ? p.slice(idx) : p
  }

  return (
    <div className={`border rounded-lg overflow-hidden transition-colors ${
      status === 'success' ? 'border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-900/10' :
      status === 'failed' ? 'border-red-300 dark:border-red-700 bg-red-50/50 dark:bg-red-900/10' :
      'border-gray-200 dark:border-gray-700'
    }`}>
      <div className="flex items-start gap-2 px-3 py-2">
        {/* Checkbox */}
        {finding.fixable && (
          <button
            onClick={onToggleSelect}
            className={`mt-0.5 w-4 h-4 border rounded flex items-center justify-center flex-shrink-0 transition-colors ${
              selected
                ? 'bg-primary-500 border-primary-500 text-white'
                : 'border-gray-300 dark:border-gray-600'
            }`}
          >
            {selected && <Check className="w-3 h-3" />}
          </button>
        )}
        {!finding.fixable && <div className="w-4 flex-shrink-0" />}

        {/* Severity badge */}
        <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded flex-shrink-0 ${SEVERITY_STYLES[finding.severity]}`}>
          {finding.severity}
        </span>

        {/* Title + expand */}
        <button onClick={onToggleExpand} className="flex-1 text-left group">
          <span className="text-xs font-medium text-gray-800 dark:text-gray-200 group-hover:text-primary-500 transition-colors">
            {finding.title}
          </span>
        </button>

        {/* Status badge */}
        {status === 'success' && <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />}
        {status === 'failed' && <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}

        {/* Expand arrow */}
        <button onClick={onToggleExpand} className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
      </div>

      {/* Document chips */}
      <div className="flex flex-wrap gap-1 px-3 pb-2">
        {finding.documents.slice(0, 8).map(docId => (
          <span key={docId} className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-[9px] font-mono rounded">
            {docId.replace('WP-IDEA-', '')}
          </span>
        ))}
        {finding.documents.length > 8 && (
          <span className="px-1.5 py-0.5 text-gray-400 dark:text-gray-500 text-[9px]">
            +{finding.documents.length - 8} more
          </span>
        )}
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2 space-y-2">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">Description</p>
            <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-line">{finding.description}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">Recommendation</p>
            <p className="text-xs text-gray-700 dark:text-gray-300">{finding.recommendation}</p>
          </div>

          {/* Fix preview */}
          {finding.fixes && finding.fixes.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">
                Fixes ({finding.fixes.length})
              </p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {finding.fixes.map((fix, i) => (
                  <div key={i} className="text-[10px] font-mono bg-gray-50 dark:bg-gray-800 rounded p-1.5 border border-gray-200 dark:border-gray-700">
                    <p className="text-gray-400 dark:text-gray-500 mb-1 truncate" title={fix.file}>
                      {shortPath(fix.file)}
                    </p>
                    <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 px-1 py-0.5 rounded line-through mb-0.5 break-all">
                      {fix.oldText.length > 120 ? fix.oldText.slice(0, 120) + '...' : fix.oldText}
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 px-1 py-0.5 rounded break-all">
                      {fix.newText.length > 120 ? fix.newText.slice(0, 120) + '...' : fix.newText}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Cross-Correlation Section ───
function CrossCorrelationSection({ data, queryClient }: { data: TrackerData; queryClient: ReturnType<typeof useQueryClient> }) {
  const docs = data.documents.filter(d => d.status !== 'backlog' && d.status !== 'hold')
  const ids = docs.map(d => d.id)
  const shortIds = docs.map(d =>
    d.id.startsWith('WP-IDEA-') ? d.id.replace('WP-IDEA-', '') : d.id
  )
  const matrix = data.crossCorrelation.matrix
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null)

  // Analysis state
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null)
  const [analysisProgress, setAnalysisProgress] = useState<AnalysisProgress | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [selectedFindings, setSelectedFindings] = useState<Set<string>>(new Set())
  const [expandedFindings, setExpandedFindings] = useState<Set<string>>(new Set())
  const [applyStatus, setApplyStatus] = useState<Record<string, 'success' | 'failed'>>({})
  const [isApplying, setIsApplying] = useState(false)

  const runAnalysis = useCallback(async () => {
    console.log('[CrossCorrelation] runAnalysis called')
    setIsAnalyzing(true)
    setAnalysisResult(null)
    setAnalysisProgress(null)
    setSelectedFindings(new Set())
    setExpandedFindings(new Set())
    setApplyStatus({})

    try {
      console.log('[CrossCorrelation] fetching /api/tracker/analyze...')
      const response = await fetch('/api/tracker/analyze', { method: 'POST' })
      console.log('[CrossCorrelation] response:', response.status, response.ok, !!response.body)
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`API error ${response.status}: ${errorText.slice(0, 200)}`)
      }
      if (!response.body) throw new Error('No response body for SSE stream')
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        let currentEvent = ''
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7)
          } else if (line.startsWith('data: ')) {
            try {
              const eventData = JSON.parse(line.slice(6))
              if (currentEvent === 'progress') {
                setAnalysisProgress(eventData)
              } else if (currentEvent === 'complete') {
                setAnalysisResult(eventData)
                const fixableIds = new Set<string>(
                  eventData.findings
                    .filter((f: AnalysisFinding) => f.fixable)
                    .map((f: AnalysisFinding) => f.id)
                )
                setSelectedFindings(fixableIds)
              }
            } catch {
              // ignore parse errors from partial chunks
            }
          }
        }
      }
    } catch (err) {
      console.error('Analysis request failed:', err)
      setAnalysisProgress({ step: 'Error', percent: 0, message: err instanceof Error ? err.message : 'Analysis failed', findingsCount: 0 })
      setTimeout(() => setAnalysisProgress(null), 5000)
    } finally {
      setIsAnalyzing(false)
    }
  }, [])

  const applyFixes = useCallback(async () => {
    if (!analysisResult || selectedFindings.size === 0) return
    setIsApplying(true)

    try {
      const resp = await fetch('/api/tracker/analyze/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          findingIds: Array.from(selectedFindings),
          findings: analysisResult.findings,
        }),
      })
      const result = await resp.json()

      const statusMap: Record<string, 'success' | 'failed'> = {}
      result.applied?.forEach((a: { findingId: string }) => { statusMap[a.findingId] = 'success' })
      result.failed?.forEach((f: { findingId: string }) => { statusMap[f.findingId] = 'failed' })
      setApplyStatus(statusMap)
      queryClient.invalidateQueries({ queryKey: ['tracker'] })
    } catch (err) {
      console.error('Apply failed:', err)
    } finally {
      setIsApplying(false)
    }
  }, [analysisResult, selectedFindings, queryClient])

  const toggleFinding = (id: string) => {
    setSelectedFindings(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleExpand = (id: string) => {
    setExpandedFindings(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const fixableFindings = analysisResult?.findings.filter(f => f.fixable) || []
  const allFixableSelected = fixableFindings.length > 0 && fixableFindings.every(f => selectedFindings.has(f.id))

  const toggleAllFixable = () => {
    if (allFixableSelected) {
      setSelectedFindings(new Set())
    } else {
      setSelectedFindings(new Set(fixableFindings.map(f => f.id)))
    }
  }

  return (
    <div className="space-y-4">
      {/* Header with Run Analysis button */}
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-4 text-[10px] text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 inline-block bg-green-500" /> Fresh</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 inline-block bg-amber-500" /> Stale &lt;7d</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 inline-block bg-red-500" /> Stale &gt;7d</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 inline-block bg-gray-200 dark:bg-gray-700" /> Never checked</span>
        </div>
        <button
          onClick={runAnalysis}
          disabled={isAnalyzing}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-400 text-white text-xs font-medium rounded transition-colors"
        >
          {isAnalyzing ? (
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Shield className="w-3.5 h-3.5" />
          )}
          {isAnalyzing ? 'Analyzing...' : 'Run Analysis'}
        </button>
      </div>

      {/* Progress bar */}
      {(isAnalyzing || analysisProgress?.step === 'Error') && analysisProgress && (
        <div className={`glass-card p-3 space-y-2 ${analysisProgress.step === 'Error' ? 'border border-red-300 dark:border-red-700' : ''}`}>
          <div className="flex items-center justify-between text-xs">
            <span className={`font-medium ${analysisProgress.step === 'Error' ? 'text-red-500 dark:text-red-400' : 'text-gray-600 dark:text-gray-300'}`}>{analysisProgress.step === 'Error' ? analysisProgress.message : analysisProgress.step}</span>
            <span className="text-gray-400 dark:text-gray-500 font-mono">{analysisProgress.step !== 'Error' && `${analysisProgress.percent}%`}</span>
          </div>
          {analysisProgress.step !== 'Error' && <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 transition-all duration-300 rounded-full"
              style={{ width: `${analysisProgress.percent}%` }}
            />
          </div>}
          <p className="text-[10px] text-gray-400 dark:text-gray-500">
            {analysisProgress.message} ({analysisProgress.findingsCount} findings so far)
          </p>
        </div>
      )}

      {/* Heatmap */}
      <div className="glass-card overflow-x-auto relative" style={{ padding: '12px' }}>
        <table className="border-collapse">
          <thead>
            <tr>
              <th className="w-14" />
              {shortIds.map((sid, i) => (
                <th
                  key={ids[i]}
                  className="text-[8px] font-mono font-normal text-gray-500 dark:text-gray-400 px-0.5 pb-1"
                  style={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)', height: '60px' }}
                  title={ids[i]}
                >
                  {sid}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ids.map((rowId, ri) => (
              <tr key={rowId}>
                <th className="text-[8px] font-mono font-normal text-gray-500 dark:text-gray-400 text-right pr-2 whitespace-nowrap" title={rowId}>
                  {shortIds[ri]}
                </th>
                {ids.map((colId, ci) => {
                  if (ri === ci) {
                    return <td key={colId} className="w-[18px] h-[18px] bg-gray-400 dark:bg-gray-600 border border-white dark:border-surface-900" />
                  }
                  const entry = matrix[rowId]?.[colId]
                  let bg = 'bg-gray-200 dark:bg-gray-700'
                  let tip = `${rowId} vs ${colId}: Never checked`
                  if (entry?.lastChecked) {
                    if (entry.fresh) {
                      bg = 'bg-green-500'
                      tip = `${rowId} vs ${colId}: Fresh (${entry.lastChecked})`
                    } else {
                      const days = Math.floor((Date.now() - new Date(entry.lastChecked).getTime()) / 86400000)
                      bg = days <= 7 ? 'bg-amber-500' : 'bg-red-500'
                      tip = `${rowId} vs ${colId}: Stale ${days}d (${entry.lastChecked})`
                    }
                  }
                  return (
                    <td
                      key={colId}
                      className={`w-[18px] h-[18px] ${bg} border border-white dark:border-surface-900 cursor-default`}
                      onMouseEnter={e => setTooltip({ text: tip, x: e.clientX, y: e.clientY })}
                      onMouseLeave={() => setTooltip(null)}
                    />
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>

        {tooltip && (
          <div
            className="fixed z-50 px-2 py-1 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-[10px] font-mono pointer-events-none"
            style={{ left: tooltip.x + 12, top: tooltip.y - 8 }}
          >
            {tooltip.text}
          </div>
        )}
      </div>

      {/* Analysis Results */}
      {analysisResult && (
        <div className="space-y-3">
          {/* Summary bar */}
          <div className="glass-card p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4 text-gray-500" />
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    {analysisResult.summary.total} findings
                  </span>
                </div>
                {analysisResult.summary.high > 0 && (
                  <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${SEVERITY_STYLES.HIGH}`}>
                    {analysisResult.summary.high} HIGH
                  </span>
                )}
                {analysisResult.summary.medium > 0 && (
                  <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${SEVERITY_STYLES.MEDIUM}`}>
                    {analysisResult.summary.medium} MED
                  </span>
                )}
                {analysisResult.summary.low > 0 && (
                  <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${SEVERITY_STYLES.LOW}`}>
                    {analysisResult.summary.low} LOW
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 text-[10px] text-gray-400 dark:text-gray-500 font-mono">
                <span>{analysisResult.documentsScanned} docs scanned</span>
                <span>{analysisResult.durationMs}ms</span>
              </div>
            </div>
          </div>

          {/* Select all / deselect all */}
          {fixableFindings.length > 0 && (
            <div className="flex items-center justify-between px-1">
              <button
                onClick={toggleAllFixable}
                className="text-[10px] font-medium text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
              >
                {allFixableSelected ? 'Deselect all fixable' : `Select all fixable (${fixableFindings.length})`}
              </button>
              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                {selectedFindings.size} of {fixableFindings.length} fixable selected
              </span>
            </div>
          )}

          {/* Finding cards */}
          <div className="space-y-2">
            {analysisResult.findings.map(finding => (
              <FindingCard
                key={finding.id}
                finding={finding}
                selected={selectedFindings.has(finding.id)}
                expanded={expandedFindings.has(finding.id)}
                status={applyStatus[finding.id]}
                onToggleSelect={() => toggleFinding(finding.id)}
                onToggleExpand={() => toggleExpand(finding.id)}
              />
            ))}
          </div>

          {/* Apply button */}
          {fixableFindings.length > 0 && selectedFindings.size > 0 && Object.keys(applyStatus).length === 0 && (
            <div className="flex justify-end">
              <button
                onClick={applyFixes}
                disabled={isApplying}
                className="flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-xs font-medium rounded transition-colors"
              >
                {isApplying ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Check className="w-3.5 h-3.5" />
                )}
                {isApplying ? 'Applying...' : `Apply ${selectedFindings.size} Selected Fixes`}
              </button>
            </div>
          )}

          {/* Post-apply summary */}
          {Object.keys(applyStatus).length > 0 && (
            <div className="glass-card p-3">
              <div className="flex items-center gap-2 text-xs">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span className="text-gray-700 dark:text-gray-300 font-medium">
                  {Object.values(applyStatus).filter(s => s === 'success').length} fixes applied
                </span>
                {Object.values(applyStatus).filter(s => s === 'failed').length > 0 && (
                  <>
                    <span className="text-gray-400">|</span>
                    <XCircle className="w-4 h-4 text-red-500" />
                    <span className="text-red-600 dark:text-red-400">
                      {Object.values(applyStatus).filter(s => s === 'failed').length} failed
                    </span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Relationship Analysis Types ───
interface DiscoveredRelationship {
  source: string
  target: string
  type: string
  description: string
  bidirectional: boolean
  strength: number
  evidence: string[]
  isNew: boolean
}

interface RelationshipAnalysisResult {
  relationships: DiscoveredRelationship[]
  documentsWith3BP: { id: string; title: string; mentions: number }[]
  documentsWithout3BP: { id: string; title: string }[]
  totalDocumentsScanned: number
  durationMs: number
}

// ─── Interrelation Graph (SVG) ───
function InterrelationGraph({ data }: { data: TrackerData }) {
  const svgRef = useRef<SVGSVGElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({ width: 900, height: 600 })
  const [hoveredNode, setHoveredNode] = useState<string | null>(null)
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const queryClient = useQueryClient()

  // Analysis state
  const [analysisResult, setAnalysisResult] = useState<RelationshipAnalysisResult | null>(null)
  const [analysisProgress, setAnalysisProgress] = useState<AnalysisProgress | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [selectedNewRels, setSelectedNewRels] = useState<Set<string>>(new Set())
  const [isSaving, setIsSaving] = useState(false)
  const [saveResult, setSaveResult] = useState<{ added: number; updated: number } | null>(null)

  useEffect(() => {
    if (containerRef.current) {
      const w = containerRef.current.clientWidth - 32
      setDimensions({ width: Math.max(600, w), height: 600 })
    }
  }, [])

  // Build document title lookup
  const docTitles = useMemo(() => {
    const map: Record<string, string> = {}
    data.documents.forEach(d => { map[d.id] = d.title })
    return map
  }, [data.documents])

  const docShortTitles = useMemo(() => {
    const map: Record<string, string> = {}
    data.documents.forEach(d => { map[d.id] = d.shortTitle })
    return map
  }, [data.documents])

  const { nodes, edges } = useMemo(() => {
    const docs = data.documents
    const idToIdx: Record<string, number> = {}
    const cx = dimensions.width / 2
    const cy = dimensions.height / 2

    // Central 3BP node at index 0
    const centerNode = {
      id: '3BP',
      shortId: '3BP',
      title: 'The Three-Body Problem',
      status: 'center' as string,
      x: cx,
      y: cy,
      vx: 0,
      vy: 0,
      radius: 28,
      connections: docs.length,
      isCenter: true,
    }
    idToIdx['3BP'] = 0

    const docNodes = docs.map((d, i) => {
      idToIdx[d.id] = i + 1
      const conns = data.interrelations.filter(r => r.source === d.id || r.target === d.id).length
      const angle = (i / docs.length) * Math.PI * 2
      const dist = dimensions.width * 0.3
      return {
        id: d.id,
        shortId: d.id.startsWith('WP-IDEA-') ? d.id.replace('WP-IDEA-', '') : d.id,
        title: d.title,
        status: d.status,
        x: cx + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist,
        vx: 0,
        vy: 0,
        radius: Math.max(14, 10 + conns * 2),
        connections: conns,
        isCenter: false,
      }
    })

    const nodes = [centerNode, ...docNodes]

    // Build edges: real interrelations + implicit connections to center
    const realEdges = data.interrelations
      .map(r => ({
        sourceIdx: idToIdx[r.source] as number | undefined,
        targetIdx: idToIdx[r.target] as number | undefined,
        type: r.type,
        description: r.description,
        source: r.source,
        target: r.target,
        isCenter: false,
      }))
      .filter((e): e is { sourceIdx: number; targetIdx: number; type: string; description: string; source: string; target: string; isCenter: boolean } =>
        e.sourceIdx !== undefined && e.targetIdx !== undefined
      )

    // Implicit edges from every doc to center
    const centerEdges = docs.map((d, i) => ({
      sourceIdx: 0,
      targetIdx: i + 1,
      type: 'part-of',
      description: `${d.shortTitle} is part of the 3BP framework`,
      source: '3BP',
      target: d.id,
      isCenter: true,
    }))

    const allEdges = [...centerEdges, ...realEdges]

    // Force simulation — pin center node
    const REPULSION = 10000, SPRING = 0.025, REST = 120, DAMP = 0.82
    const CENTER_PULL = 0.006
    const CENTER_SPRING = 0.01, CENTER_REST = 180

    for (let tick = 0; tick < 500; tick++) {
      // Repulsion between non-center nodes
      for (let i = 1; i < nodes.length; i++) {
        const ni = nodes[i]!
        for (let j = i + 1; j < nodes.length; j++) {
          const nj = nodes[j]!
          const dx = nj.x - ni.x
          const dy = nj.y - ni.y
          const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1)
          const f = REPULSION / (dist * dist)
          const fx = (dx / dist) * f
          const fy = (dy / dist) * f
          ni.vx -= fx; ni.vy -= fy
          nj.vx += fx; nj.vy += fy
        }
      }
      // Spring forces for real edges
      for (const e of realEdges) {
        if (e.sourceIdx === 0 || e.targetIdx === 0) continue
        const s = nodes[e.sourceIdx]!, t = nodes[e.targetIdx]!
        const dx = t.x - s.x, dy = t.y - s.y
        const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1)
        const f = SPRING * (dist - REST)
        const fx = (dx / dist) * f, fy = (dy / dist) * f
        s.vx += fx; s.vy += fy; t.vx -= fx; t.vy -= fy
      }
      // Pull all doc nodes toward center with spring force
      for (let i = 1; i < nodes.length; i++) {
        const n = nodes[i]!
        const dx = cx - n.x, dy = cy - n.y
        const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1)
        const f = CENTER_SPRING * (dist - CENTER_REST)
        n.vx += (dx / dist) * f
        n.vy += (dy / dist) * f
        // Gentle center gravity
        n.vx += dx * CENTER_PULL
        n.vy += dy * CENTER_PULL
      }
      // Apply velocity and damping (skip center node)
      for (let i = 1; i < nodes.length; i++) {
        const n = nodes[i]!
        n.vx *= DAMP; n.vy *= DAMP
        n.x += n.vx; n.y += n.vy
        n.x = Math.max(n.radius + 10, Math.min(dimensions.width - n.radius - 10, n.x))
        n.y = Math.max(n.radius + 10, Math.min(dimensions.height - n.radius - 10, n.y))
      }
    }

    return { nodes, edges: allEdges }
  }, [data, dimensions])

  const nodeColors: Record<string, string> = {
    center: '#d97706',
    final: '#22c55e',
    partial: '#f59e0b',
    outlined: '#3b82f6',
    review: '#3b82f6',
    'white-paper-only': '#a855f7',
    backlog: '#d1d5db',
    hold: '#ef4444',
  }

  const connectedIds = useMemo(() => {
    if (!hoveredNode) return null
    const set = new Set([hoveredNode])
    if (hoveredNode === '3BP') {
      // Center connects to everything
      data.documents.forEach(d => set.add(d.id))
    } else {
      set.add('3BP')
      data.interrelations.forEach(r => {
        if (r.source === hoveredNode) set.add(r.target)
        if (r.target === hoveredNode) set.add(r.source)
      })
    }
    return set
  }, [hoveredNode, data.interrelations, data.documents])

  // Get connections for hovered node (for tooltip)
  const hoveredConnections = useMemo(() => {
    if (!hoveredNode || hoveredNode === '3BP') return []
    return data.interrelations
      .filter(r => r.source === hoveredNode || r.target === hoveredNode)
      .map(r => ({
        otherId: r.source === hoveredNode ? r.target : r.source,
        type: r.type,
        direction: r.source === hoveredNode ? 'out' : 'in',
      }))
  }, [hoveredNode, data.interrelations])

  const handleNodeHover = useCallback((nodeId: string | null, event?: React.MouseEvent) => {
    setHoveredNode(nodeId)
    if (event) {
      setTooltipPos({ x: event.clientX, y: event.clientY })
    }
  }, [])

  // Run relationship analysis
  const runRelationshipAnalysis = useCallback(async () => {
    setIsAnalyzing(true)
    setAnalysisResult(null)
    setAnalysisProgress(null)
    setSelectedNewRels(new Set())
    setSaveResult(null)

    try {
      const response = await fetch('/api/tracker/analyze-relationships', { method: 'POST' })
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`API error ${response.status}: ${errorText.slice(0, 200)}`)
      }
      if (!response.body) throw new Error('No response body for SSE stream')
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        let currentEvent = ''
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7)
          } else if (line.startsWith('data: ')) {
            try {
              const eventData = JSON.parse(line.slice(6))
              if (currentEvent === 'progress') {
                setAnalysisProgress(eventData)
              } else if (currentEvent === 'complete') {
                setAnalysisResult(eventData)
                // Auto-select all new relationships
                const newRels = new Set<string>(
                  eventData.relationships
                    .filter((r: DiscoveredRelationship) => r.isNew)
                    .map((r: DiscoveredRelationship) => `${r.source}::${r.target}`)
                )
                setSelectedNewRels(newRels)
              }
            } catch {
              // ignore parse errors
            }
          }
        }
      }
    } catch (err) {
      console.error('Relationship analysis failed:', err)
      setAnalysisProgress({ step: 'Error', percent: 0, message: err instanceof Error ? err.message : 'Relationship analysis failed', findingsCount: 0 })
      setTimeout(() => setAnalysisProgress(null), 5000)
    } finally {
      setIsAnalyzing(false)
    }
  }, [])

  // Save selected new relationships
  const saveSelectedRelationships = useCallback(async () => {
    if (!analysisResult || selectedNewRels.size === 0) return
    setIsSaving(true)

    const toSave = analysisResult.relationships
      .filter(r => selectedNewRels.has(`${r.source}::${r.target}`))
      .map(r => ({
        source: r.source,
        target: r.target,
        type: r.type,
        description: r.description,
        bidirectional: r.bidirectional,
      }))

    try {
      const resp = await fetch('/api/tracker/interrelations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ relationships: toSave }),
      })
      const result = await resp.json()
      setSaveResult(result)
      queryClient.invalidateQueries({ queryKey: ['tracker'] })
    } catch (err) {
      console.error('Save failed:', err)
    } finally {
      setIsSaving(false)
    }
  }, [analysisResult, selectedNewRels, queryClient])

  const newRelCount = analysisResult?.relationships.filter(r => r.isNew).length ?? 0

  return (
    <div className="space-y-4">
      {/* Legend + Run Analysis button */}
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-3 text-[10px] text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#d97706' }} />
            3BP Center
          </span>
          {Object.entries(EDGE_COLORS).map(([type, color]) => (
            <span key={type} className="flex items-center gap-1">
              <span className="inline-block w-4 h-0.5" style={{ background: color }} />
              {type}
            </span>
          ))}
          <span className="flex items-center gap-1">
            <span className="inline-block w-4 h-0.5 border-t border-dashed border-amber-400" />
            part-of
          </span>
        </div>
        <button
          onClick={runRelationshipAnalysis}
          disabled={isAnalyzing}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-400 text-white text-xs font-medium rounded transition-colors"
        >
          {isAnalyzing ? (
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <GitBranch className="w-3.5 h-3.5" />
          )}
          {isAnalyzing ? 'Analyzing...' : 'Run Relationship Analysis'}
        </button>
      </div>

      {/* Progress bar */}
      {(isAnalyzing || analysisProgress?.step === 'Error') && analysisProgress && (
        <div className={`glass-card p-3 space-y-2 ${analysisProgress.step === 'Error' ? 'border border-red-300 dark:border-red-700' : ''}`}>
          <div className="flex items-center justify-between text-xs">
            <span className={`font-medium ${analysisProgress.step === 'Error' ? 'text-red-500 dark:text-red-400' : 'text-gray-600 dark:text-gray-300'}`}>{analysisProgress.step === 'Error' ? analysisProgress.message : analysisProgress.step}</span>
            <span className="text-gray-400 dark:text-gray-500 font-mono">{analysisProgress.step !== 'Error' && `${analysisProgress.percent}%`}</span>
          </div>
          {analysisProgress.step !== 'Error' && <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full bg-primary-500 transition-all duration-300 rounded-full" style={{ width: `${analysisProgress.percent}%` }} />
          </div>}
          <p className="text-[10px] text-gray-400 dark:text-gray-500">{analysisProgress.step !== 'Error' && analysisProgress.message}</p>
        </div>
      )}

      {/* Status legend (node colors) */}
      <div className="flex flex-wrap gap-3 text-[10px] text-gray-500 dark:text-gray-400">
        {[
          { status: 'final', label: 'Final', color: '#22c55e' },
          { status: 'partial', label: 'In Progress', color: '#f59e0b' },
          { status: 'outlined', label: 'Outlined', color: '#3b82f6' },
          { status: 'white-paper-only', label: 'White Paper', color: '#a855f7' },
          { status: 'backlog', label: 'Backlog', color: '#d1d5db' },
          { status: 'hold', label: 'On Hold', color: '#ef4444' },
        ].map(s => (
          <span key={s.status} className="flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>

      {/* Graph */}
      <div ref={containerRef} className="glass-card overflow-x-auto relative" style={{ padding: '16px' }}>
        <svg
          ref={svgRef}
          width={dimensions.width}
          height={dimensions.height}
          viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        >
          <defs>
            {Object.entries(EDGE_COLORS).map(([type, color]) => (
              <marker
                key={type}
                id={`arrow-${type}`}
                viewBox="0 0 10 10"
                refX="10"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
              </marker>
            ))}
            {/* Glow filter for center node */}
            <filter id="glow-center">
              <feGaussianBlur stdDeviation="4" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Center connections (dashed, subtle) */}
          {edges.filter(e => e.isCenter).map((e, i) => {
            const s = nodes[e.sourceIdx]!, t = nodes[e.targetIdx]!
            const dx = t.x - s.x, dy = t.y - s.y
            const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1)
            const nx = dx / dist, ny = dy / dist
            const x1 = s.x + nx * s.radius, y1 = s.y + ny * s.radius
            const x2 = t.x - nx * t.radius, y2 = t.y - ny * t.radius
            const dimmed = hoveredNode && !connectedIds?.has(e.target)

            return (
              <line
                key={`center-${i}`}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke="#d97706"
                strokeWidth={1}
                strokeOpacity={dimmed ? 0.03 : 0.15}
                strokeDasharray="4 4"
              />
            )
          })}

          {/* Real edges */}
          {edges.filter(e => !e.isCenter).map((e, i) => {
            const s = nodes[e.sourceIdx]!, t = nodes[e.targetIdx]!
            const dx = t.x - s.x, dy = t.y - s.y
            const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1)
            const nx = dx / dist, ny = dy / dist
            const x1 = s.x + nx * s.radius, y1 = s.y + ny * s.radius
            const x2 = t.x - nx * (t.radius + 6), y2 = t.y - ny * (t.radius + 6)
            const color = EDGE_COLORS[e.type] || '#999'
            const dimmed = hoveredNode && !connectedIds?.has(e.source) && !connectedIds?.has(e.target)

            return (
              <line
                key={`edge-${i}`}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={color}
                strokeWidth={1.5}
                strokeOpacity={dimmed ? 0.08 : 0.6}
                markerEnd={`url(#arrow-${e.type})`}
              />
            )
          })}

          {/* Nodes */}
          {nodes.map(n => {
            const dimmed = hoveredNode && !connectedIds?.has(n.id)
            const isHovered = hoveredNode === n.id
            return (
              <g
                key={n.id}
                style={{ cursor: 'pointer' }}
                opacity={dimmed ? 0.12 : 1}
                onMouseEnter={(e) => handleNodeHover(n.id, e)}
                onMouseMove={(e) => setTooltipPos({ x: e.clientX, y: e.clientY })}
                onMouseLeave={() => handleNodeHover(null)}
              >
                {/* Highlight ring on hover */}
                {isHovered && (
                  <circle
                    cx={n.x} cy={n.y} r={n.radius + 4}
                    fill="none"
                    stroke={n.isCenter ? '#d97706' : '#3b82f6'}
                    strokeWidth={2}
                    strokeOpacity={0.5}
                  />
                )}
                <circle
                  cx={n.x} cy={n.y} r={n.radius}
                  fill={nodeColors[n.status] || '#d1d5db'}
                  stroke={n.isCenter ? '#92400e' : 'white'}
                  strokeWidth={n.isCenter ? 3 : 2}
                  filter={n.isCenter ? 'url(#glow-center)' : undefined}
                />
                <text
                  x={n.x} y={n.y + (n.isCenter ? 4 : 3)}
                  textAnchor="middle"
                  className={`font-mono font-bold pointer-events-none ${
                    n.isCenter
                      ? 'text-[11px] fill-white'
                      : 'text-[9px] fill-gray-900 dark:fill-white'
                  }`}
                >
                  {n.shortId}
                </text>
              </g>
            )
          })}
        </svg>

        {/* Hover tooltip */}
        {hoveredNode && (
          <div
            className="fixed z-50 pointer-events-none"
            style={{ left: tooltipPos.x + 16, top: tooltipPos.y - 12 }}
          >
            <div className="bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 px-3 py-2 rounded shadow-lg max-w-xs">
              {hoveredNode === '3BP' ? (
                <>
                  <div className="text-xs font-bold mb-1">The Three-Body Problem</div>
                  <div className="text-[10px] opacity-80">
                    Central framework — all {data.documents.length} documents
                  </div>
                </>
              ) : (
                <>
                  <div className="text-[10px] font-mono text-amber-400 dark:text-amber-600 mb-0.5">{hoveredNode}</div>
                  <div className="text-xs font-bold mb-1">{docShortTitles[hoveredNode] || hoveredNode}</div>
                  <div className="text-[10px] opacity-70 mb-1 leading-tight">{docTitles[hoveredNode]}</div>
                  {hoveredConnections.length > 0 && (
                    <div className="border-t border-gray-700 dark:border-gray-300 pt-1 mt-1">
                      <div className="text-[9px] font-bold uppercase tracking-wider opacity-60 mb-0.5">
                        Connections ({hoveredConnections.length})
                      </div>
                      {hoveredConnections.slice(0, 6).map((c, i) => (
                        <div key={i} className="text-[9px] opacity-80 flex items-center gap-1">
                          <span className="font-mono">{c.direction === 'out' ? '\u2192' : '\u2190'}</span>
                          <span className="font-mono">{c.otherId.replace('WP-IDEA-', '')}</span>
                          <span className="italic opacity-60">({c.type})</span>
                        </div>
                      ))}
                      {hoveredConnections.length > 6 && (
                        <div className="text-[9px] opacity-50">+{hoveredConnections.length - 6} more</div>
                      )}
                    </div>
                  )}
                  {hoveredConnections.length === 0 && (
                    <div className="text-[9px] opacity-50 italic">No tracked interrelations</div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Analysis Results */}
      {analysisResult && (
        <div className="space-y-3">
          {/* 3BP Coverage Summary */}
          <div className="glass-card p-4">
            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-2">Three-Body Problem Coverage</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center">
                <div className="text-2xl font-mono font-bold text-green-600 dark:text-green-400">
                  {analysisResult.documentsWith3BP.length}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Reference 3BP
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-mono font-bold text-red-600 dark:text-red-400">
                  {analysisResult.documentsWithout3BP.length}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Missing 3BP
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-mono font-bold text-blue-600 dark:text-blue-400">
                  {analysisResult.relationships.length}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Cross-Refs Found
                </div>
              </div>
            </div>

            {/* Documents without 3BP references */}
            {analysisResult.documentsWithout3BP.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className="text-[10px] font-bold uppercase tracking-wider text-red-500 dark:text-red-400 mb-1">
                  Documents Missing Three-Body Problem References
                </p>
                <div className="flex flex-wrap gap-1">
                  {analysisResult.documentsWithout3BP.map(d => (
                    <span key={d.id} className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-[9px] font-mono rounded" title={d.title}>
                      {d.id.replace('WP-IDEA-', '')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Documents with 3BP - top mentions */}
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <p className="text-[10px] font-bold uppercase tracking-wider text-green-500 dark:text-green-400 mb-1">
                Documents With 3BP References (by mention count)
              </p>
              <div className="flex flex-wrap gap-1">
                {analysisResult.documentsWith3BP
                  .sort((a, b) => b.mentions - a.mentions)
                  .map(d => (
                    <span key={d.id} className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[9px] font-mono rounded" title={`${d.title} (${d.mentions} mentions)`}>
                      {d.id.replace('WP-IDEA-', '')} <span className="opacity-60">({d.mentions})</span>
                    </span>
                  ))}
              </div>
            </div>
          </div>

          {/* New discovered relationships */}
          {newRelCount > 0 && (
            <div className="glass-card p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200">
                  New Relationships Discovered ({newRelCount})
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                    {selectedNewRels.size} selected
                  </span>
                  {!saveResult && (
                    <button
                      onClick={saveSelectedRelationships}
                      disabled={isSaving || selectedNewRels.size === 0}
                      className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-xs font-medium rounded transition-colors"
                    >
                      {isSaving ? (
                        <RefreshCw className="w-3 h-3 animate-spin" />
                      ) : (
                        <Check className="w-3 h-3" />
                      )}
                      {isSaving ? 'Saving...' : 'Save to Tracker'}
                    </button>
                  )}
                </div>
              </div>

              {saveResult && (
                <div className="flex items-center gap-2 mb-2 px-2 py-1.5 bg-green-50 dark:bg-green-900/20 rounded text-xs text-green-700 dark:text-green-400">
                  <CheckCircle2 className="w-4 h-4" />
                  {saveResult.added} added, {saveResult.updated} updated
                </div>
              )}

              <div className="space-y-1 max-h-64 overflow-y-auto">
                {analysisResult.relationships
                  .filter(r => r.isNew)
                  .map(r => {
                    const key = `${r.source}::${r.target}`
                    const isSelected = selectedNewRels.has(key)
                    return (
                      <div key={key} className="flex items-start gap-2 px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-surface-800/50 rounded text-xs">
                        <button
                          onClick={() => {
                            setSelectedNewRels(prev => {
                              const next = new Set(prev)
                              if (next.has(key)) next.delete(key)
                              else next.add(key)
                              return next
                            })
                          }}
                          className={`mt-0.5 w-4 h-4 border rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                            isSelected ? 'bg-primary-500 border-primary-500 text-white' : 'border-gray-300 dark:border-gray-600'
                          }`}
                        >
                          {isSelected && <Check className="w-3 h-3" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono font-medium text-gray-700 dark:text-gray-300">{r.source}</span>
                            <span className="text-gray-400">{r.bidirectional ? '\u2194' : '\u2192'}</span>
                            <span className="font-mono font-medium text-gray-700 dark:text-gray-300">{r.target}</span>
                            <span className="px-1 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-[9px] rounded">{r.type}</span>
                          </div>
                          <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate" title={r.description}>
                            {r.description}
                          </div>
                          {r.evidence.length > 0 && (
                            <div className="text-[9px] text-gray-400 dark:text-gray-500 italic truncate mt-0.5" title={r.evidence[0]}>
                              {r.evidence[0]}
                            </div>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          <div className="w-8 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${r.strength * 100}%` }} />
                          </div>
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="glass-card p-3">
            <div className="flex items-center gap-3 text-[10px] text-gray-400 dark:text-gray-500 font-mono">
              <span>{analysisResult.totalDocumentsScanned} docs scanned</span>
              <span>{analysisResult.relationships.length} cross-references</span>
              <span>{newRelCount} new</span>
              <span>{analysisResult.relationships.length - newRelCount} existing</span>
              <span>{analysisResult.durationMs}ms</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
